<template>
  <div class="abtest-review-page">
    <AbtestStepper
      :game-id="gameId"
      :version-id="versionId"
      :abtest-id="abtestId"
      :abtest="abtest"
      :in-wip-version="inWipVersion"
      :loading="queryLoading"
    >
      <div class="abtest-details-page">
        <div v-if="abtest">
          <section class="review">
            <h3 class="priority-card">
              <span class="label">Priority</span>
              <div class="number">{{ abtest.priority }}</div>
            </h3>

            <div class="divider"></div>

            <section class="segments">
              <h3 class="segments-title">Layer</h3>
              <div>
                <VdIcon name="layer-group" store="fa" size="large" />
                {{ abtest?.layer?.name }}
              </div>
            </section>

            <div class="divider"></div>

            <section class="segments">
              <h3 class="segments-title">Segments</h3>
              <AbtestAudience
                :segment-names="segmentsName"
                :platform="abtest.platform"
                :is-dynamic="abtest.isDynamic"
              />
              <DynamicAbSegments v-if="abtest.isDynamic" :dynamic-segments-name="dynamicSegmentsName" />
            </section>

            <div class="divider"></div>

            <section class="immutability-info">
              <h3 class="immutability-title">
                <vd-custom-icon
                  v-if="abtest.isImmutable"
                  name="lock-icon"
                  width="14"
                  box="0 0 10 14"
                  color="var(--vd-color-grey)"
                />
                <span v-if="abtest.isImmutable">&nbsp;&nbsp;</span>
                <span v-if="abtest.isDynamic">Dynamic A/B Test</span>
                <span v-else>{{ abtest.isImmutable ? "Immutable" : "Mutable" }} A/B Test</span>
              </h3>
            </section>
          </section>

          <AbtestDetails
            :abtest="abtest"
            :game-id="gameId"
            :version-id="versionId"
            :offers-by-id="offersById"
            :campaign-names-by-id="campaignNamesById"
          />

          <section class="abtest-actions">
            <vd-button type="secondary" icon-left="fa:chevron-left" @click="onGoBackToListPage">Back to list</vd-button>
            <div class="step-controls">
              <vd-button type="secondary" skin="blue" @click="onGoToPreviousPage">Previous</vd-button>
              <vd-button
                type="primary"
                :loading="onReadyToPublishLoading"
                :disabled="!abtest"
                @click="onMarkAsReadyToPublish"
              >
                Mark as ready for publishing
              </vd-button>
            </div>
          </section>
        </div>
      </div>
    </AbtestStepper>
  </div>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { computed } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";

import AbtestStepper from "@/components/AbtestStepper/AbtestStepper.vue";
import { AbtestState, useAbtestReviewPageQuery, useAbtestReviewPageUpdateStateMutation } from "@/graphql";
import AbtestAudience from "@/pages/games/GameDisplay/children/abtests/components/AbtestAudience.vue";
import AbtestDetails from "@/pages/games/GameDisplay/children/abtests/components/AbtestDetails.vue";
import useTreatmentOffersData from "@/modules/Offers/hooks/useTreatmentOffersData";
import DynamicAbSegments from "@/pages/games/GameDisplay/children/abtests/components/DynamicAbSegments.vue";
import links from "@/router/links";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  abtestId: {
    type: String,
    required: true,
  },
});
const router = useRouter();

// Offer/campaign lookups for the cohort treatments display, fetched once here instead of per treatment.
const { offersById, campaignNamesById } = useTreatmentOffersData(props.gameId, props.versionId);

/**
 * If we have a abtestId in props then fetch the abtest.
 */
const { result, loading: queryLoading } = useAbtestReviewPageQuery({
  gameId: props.gameId,
  versionId: props.versionId,
  abtestId: props.abtestId,
});

const abtest = computed(() => result.value?.game?.version?.abtest ?? undefined);
const inWipVersion = computed(() => result.value?.game?.version?.isWip ?? false);
const segmentsName = computed(() => {
  const segments = abtest.value?.segments ?? [];
  return segments.map(({ name }) => name);
});
const dynamicSegmentsName = computed(() => {
  const segments = abtest.value?.dynamicSegments ?? [];
  return segments.map(({ name }) => name);
});

/**
 * Links
 */
const backToListLink: RouteLocationRaw = {
  name: links.games.abtests.list,
  params: {
    gameId: props.gameId,
    versionId: props.versionId,
  },
};

const onGoBackToListPage = () => {
  router.push(backToListLink);
};

const onGoToPreviousPage = () => {
  router.push({
    name: links.games.abtests.cohorts.list,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
    },
  });
};

/**
 * Publishing an abtest
 */
const { mutate: updateAbtestState, loading: onReadyToPublishLoading } = useAbtestReviewPageUpdateStateMutation({});

const onMarkAsReadyToPublish = async () => {
  await updateAbtestState({
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
    state: AbtestState.Pending,
  });
  router.push(backToListLink);
};
</script>

<style lang="scss" scoped>
.abtest-details-page {
  .review {
    display: flex;
    margin-bottom: size("ML");
    padding-bottom: size("ML");
    border-bottom: 1px solid $foggy-grey;

    .divider {
      width: 1px;
      background-color: $foggy-grey;
      box-sizing: content-box;
      margin: 0 size("ML");
    }

    h3 {
      margin-bottom: size("ML");
    }

    .priority-card {
      @extend %priority-card;
      margin: 0;
    }

    .immutability-info {
      display: flex;
      align-items: center;
    }

    .segments-title,
    .immutability-title {
      font-weight: 600;
      margin-bottom: 15px;
    }
    .immutable-info {
      font-weight: 600;
      margin-left: size("L");
    }
  }

  .abtest-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .step-controls {
      display: flex;
      align-items: center;

      .vd-button {
        margin-left: size("M");
      }
    }
  }
}
</style>
